import { Search, X } from 'lucide-react'
import { useEffect, useId, useState } from 'react'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  SelectSearch,
  type SelectSearchOption,
} from '@/components/ui/select-search'
import type { DriverLite } from './planning-types'

/**
 * Planning filters bar.
 *
 * Three controls:
 *   - **Date preset** — quick choices ("Aujourd'hui", "Demain",
 *     "Cette semaine", "Semaine prochaine") + a custom date
 *     fallback. Each preset writes into the `dateFrom` /
 *     `dateTo` fields of the parent.
 *   - **Driver** — searchable select over the tenant's drivers.
 *   - **Search** — free text on the site name. Debounced 300 ms
 *     so each keystroke doesn't trigger a refetch.
 *
 * The component is fully controlled: parent owns the values, we
 * just emit on change.
 */

export type DatePreset = 'today' | 'tomorrow' | 'thisWeek' | 'nextWeek' | 'custom'

export interface PlanningFiltersValue {
  preset: DatePreset
  dateFrom: string
  dateTo: string
  driverId: string | null
  search: string
}

interface PlanningFiltersProps {
  value: PlanningFiltersValue
  onChange: (next: PlanningFiltersValue) => void
  drivers: readonly DriverLite[]
  driversLoading?: boolean
}

const PRESETS: ReadonlyArray<{ id: DatePreset; label: string }> = [
  { id: 'today', label: "Aujourd'hui" },
  { id: 'tomorrow', label: 'Demain' },
  { id: 'thisWeek', label: 'Cette semaine' },
  { id: 'nextWeek', label: 'Semaine prochaine' },
  { id: 'custom', label: 'Date personnalisée' },
]

export function PlanningFilters({
  value,
  onChange,
  drivers,
  driversLoading = false,
}: PlanningFiltersProps) {
  const presetId = useId()
  const driverId = useId()
  const searchId = useId()

  // Search input is debounced locally so we don't refetch on
  // every keystroke. The committed search value lives in `value.
  // search`; the local input mirrors it and `useEffect` flushes
  // after 300 ms of inactivity.
  const [searchDraft, setSearchDraft] = useState(value.search)
  useEffect(() => {
    if (searchDraft === value.search) return
    const t = setTimeout(() => onChange({ ...value, search: searchDraft }), 300)
    return () => clearTimeout(t)
  }, [searchDraft, value, onChange])

  const driverOptions: SelectSearchOption[] = drivers.map((d) => {
    const opt: SelectSearchOption = { value: d.id, label: d.name }
    if (d.email !== '') opt.detail = d.email
    return opt
  })

  function handlePreset(preset: DatePreset): void {
    if (preset === 'custom') {
      onChange({ ...value, preset })
      return
    }
    const range = computeRange(preset)
    onChange({
      ...value,
      preset,
      dateFrom: range.dateFrom,
      dateTo: range.dateTo,
    })
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
      {/* Preset */}
      <div className="sm:col-span-3 flex flex-col gap-1.5">
        <Label htmlFor={presetId}>Période</Label>
        <select
          id={presetId}
          value={value.preset}
          onChange={(e) => handlePreset(e.target.value as DatePreset)}
          className={[
            'h-10 w-full rounded-md border border-surface-border bg-surface px-3 text-sm',
            'focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand',
          ].join(' ')}
        >
          {PRESETS.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
      </div>

      {/* Custom date range — only visible when preset === 'custom' */}
      {value.preset === 'custom' ? (
        <>
          <div className="sm:col-span-2 flex flex-col gap-1.5">
            <Label htmlFor={`${presetId}-from`}>Du</Label>
            <Input
              id={`${presetId}-from`}
              type="date"
              value={value.dateFrom}
              onChange={(e) => onChange({ ...value, dateFrom: e.target.value })}
            />
          </div>
          <div className="sm:col-span-2 flex flex-col gap-1.5">
            <Label htmlFor={`${presetId}-to`}>Au</Label>
            <Input
              id={`${presetId}-to`}
              type="date"
              value={value.dateTo}
              onChange={(e) => onChange({ ...value, dateTo: e.target.value })}
            />
          </div>
        </>
      ) : null}

      {/* Driver */}
      <div className={value.preset === 'custom' ? 'sm:col-span-2 flex flex-col gap-1.5' : 'sm:col-span-3 flex flex-col gap-1.5'}>
        <Label htmlFor={driverId}>Chauffeur</Label>
        <SelectSearch
          triggerId={driverId}
          placeholder="Tous les chauffeurs"
          value={value.driverId}
          onChange={(v) => onChange({ ...value, driverId: v })}
          options={driverOptions}
          loading={driversLoading}
        />
      </div>

      {/* Search */}
      <div className={value.preset === 'custom' ? 'sm:col-span-3 flex flex-col gap-1.5' : 'sm:col-span-6 flex flex-col gap-1.5'}>
        <Label htmlFor={searchId}>Recherche chantier</Label>
        <div className="relative">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"
            aria-hidden="true"
          />
          <Input
            id={searchId}
            type="search"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            placeholder="Nom du chantier"
            className="pl-9"
          />
          {searchDraft !== '' ? (
            <button
              type="button"
              aria-label="Effacer"
              onClick={() => {
                setSearchDraft('')
                onChange({ ...value, search: '' })
              }}
              className={[
                'absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md',
                'text-text-muted hover:text-text hover:bg-surface-hover',
              ].join(' ')}
            >
              <X size={14} aria-hidden="true" />
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*  Date range presets                                                         */
/* -------------------------------------------------------------------------- */

interface DateRange {
  dateFrom: string
  dateTo: string
}

export function computeRange(preset: DatePreset): DateRange {
  const today = startOfLocalDay(new Date())

  switch (preset) {
    case 'today':
      return { dateFrom: localISO(today), dateTo: localISO(today) }
    case 'tomorrow': {
      const tomorrow = addDays(today, 1)
      return { dateFrom: localISO(tomorrow), dateTo: localISO(tomorrow) }
    }
    case 'thisWeek': {
      const monday = addDays(today, -((today.getDay() + 6) % 7))
      const sunday = addDays(monday, 6)
      return { dateFrom: localISO(monday), dateTo: localISO(sunday) }
    }
    case 'nextWeek': {
      const nextMonday = addDays(today, 7 - ((today.getDay() + 6) % 7))
      const nextSunday = addDays(nextMonday, 6)
      return { dateFrom: localISO(nextMonday), dateTo: localISO(nextSunday) }
    }
    case 'custom':
    default:
      return { dateFrom: localISO(today), dateTo: localISO(today) }
  }
}

function startOfLocalDay(d: Date): Date {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d)
  x.setDate(x.getDate() + n)
  return x
}

function localISO(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}
